import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainLayout from '../components/MainLayout';
import { CheckSquare, Clock, Loader2, CheckCircle, XCircle, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_TABS = [
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
];

const RegularizationInbox = () => {
  const [filter, setFilter] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  // Bulk Selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkModal, setBulkModal] = useState({ open: false, action: 'approve' });
  const [bulkRemarks, setBulkRemarks] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pending, approved, rejected] = await Promise.all([
        axios.get(`${API_URL}/attendance/regularization?status=pending`, { withCredentials: true }),
        axios.get(`${API_URL}/attendance/regularization?status=approved`, { withCredentials: true }),
        axios.get(`${API_URL}/attendance/regularization?status=rejected`, { withCredentials: true }),
      ]);
      setCounts({
        pending: pending.data.length,
        approved: approved.data.length,
        rejected: rejected.data.length,
      });
      const map = { pending: pending.data, approved: approved.data, rejected: rejected.data };
      setRequests(map[filter] || []);
      setSelectedIds([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load regularization requests');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === requests.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(requests.map(r => r.id));
    }
  };

  const act = async (id, status) => {
    setActingId(id);
    try {
      await axios.post(
        `${API_URL}/attendance/regularization/${id}/approve`,
        { status, approver_remarks: '' },
        { withCredentials: true },
      );
      toast.success(`Request ${status}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setActingId(null);
    }
  };

  const executeBulk = async () => {
    if (selectedIds.length === 0) return;
    const action = bulkModal.action;
    if (action === 'reject' && !bulkRemarks.trim()) {
      toast.error('Remarks are required for rejection');
      return;
    }
    setBulkProcessing(true);
    try {
      const payload = {
        action: action === 'approve' ? 'approve' : 'reject',
        remarks: bulkRemarks,
        requests: selectedIds.map(id => ({ id, approval_type: 'regularization' }))
      };
      const res = await axios.post(`${API_URL}/approvals/bulk-approve`, payload, { withCredentials: true });
      toast.success(res.data?.message || 'Bulk regularization action completed');
      setBulkModal({ open: false, action: 'approve' });
      setBulkRemarks('');
      setSelectedIds([]);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBulkProcessing(false);
    }
  };

  const typeColor = (type) => {
    const colors = {
      'Missing Punch': 'bg-red-50 text-red-700 border-red-200',
      'Wrong Punch': 'bg-amber-50 text-amber-700 border-amber-200',
      'On Duty': 'bg-blue-50 text-blue-700 border-blue-200',
      'Work From Home': 'bg-green-50 text-green-700 border-green-200',
    };
    return colors[type] || 'bg-slate-50 text-slate-700 border-slate-200';
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="regularization-inbox">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <CheckSquare className="inline-block w-8 h-8 mr-2 mb-1" />
              Regularization Inbox
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Review and approve attendance regularization requests
            </p>
          </div>
        </div>

        {/* Toolbar & Filter */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 pb-3">
          <div className="flex space-x-4">
            {STATUS_TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setFilter(t.id)}
                data-testid={`filter-${t.id}`}
                className={`pb-3 px-1 text-sm font-medium transition-colors ${
                  filter === t.id
                    ? 'text-[#0052FF] border-b-2 border-[#0052FF]'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {t.label} ({counts[t.id]})
              </button>
            ))}
          </div>

          {filter === 'pending' && requests.length > 0 && (
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedIds.length === requests.length && requests.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-slate-300 text-[#0052FF] focus:ring-[#0052FF] h-4 w-4"
                />
                Select All ({requests.length})
              </label>

              {selectedIds.length > 0 && (
                <div className="flex items-center gap-2 animate-in fade-in duration-150">
                  <span className="text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-full">
                    {selectedIds.length} Selected
                  </span>
                  <Button
                    size="sm"
                    onClick={() => { setBulkRemarks(''); setBulkModal({ open: true, action: 'approve' }); }}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                  >
                    <CheckCircle className="w-3.5 h-3.5 mr-1" /> Bulk Approve ({selectedIds.length})
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setBulkRemarks(''); setBulkModal({ open: true, action: 'reject' }); }}
                    className="border-red-300 text-red-600 hover:bg-red-50 text-xs"
                  >
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Bulk Reject ({selectedIds.length})
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500 text-sm" data-testid="reg-loading">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : requests.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-500" data-testid="reg-empty">
            <CheckSquare className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-sm">No {filter} requests yet.</p>
            <p className="text-xs text-slate-400 mt-1">
              Employees can submit regularization requests from the ESS portal — they will appear here for approval.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((r) => (
              <div
                key={r.id}
                data-testid={`regularization-request-${r.id}`}
                className={`bg-white border rounded-lg p-6 hover:shadow-sm transition-shadow ${
                  selectedIds.includes(r.id) ? 'border-blue-300 bg-blue-50/30' : 'border-slate-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  {filter === 'pending' && (
                    <div className="pt-1 pr-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(r.id)}
                        onChange={() => toggleSelect(r.id)}
                        className="rounded border-slate-300 text-[#0052FF] focus:ring-[#0052FF] h-4 w-4 cursor-pointer"
                      />
                    </div>
                  )}

                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${typeColor(r.request_type)}`}>
                        {r.request_type || 'Regularization'}
                      </span>
                      <span className="text-sm text-slate-500">
                        <Clock className="inline-block w-4 h-4 mr-1 mb-0.5" />
                        {r.created_at ? new Date(r.created_at).toLocaleString() : ''}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-slate-500">Employee</p>
                        <p className="text-sm font-medium text-slate-900">{r.employee_name || r.name || '—'}</p>
                        <p className="text-xs text-slate-500 font-mono">{r.employee_code || r.employee_id}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Date</p>
                        <p className="text-sm font-medium text-slate-900">{r.attendance_date || r.date || '—'}</p>
                      </div>
                    </div>
                    {(r.proposed_in || r.proposed_out) && (
                      <div className="mb-4">
                        <p className="text-xs text-slate-500 mb-2">Proposed Times</p>
                        <div className="flex space-x-4">
                          {r.proposed_in && (
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-slate-500">IN:</span>
                              <span className="text-sm font-mono font-medium text-green-600">{r.proposed_in}</span>
                            </div>
                          )}
                          {r.proposed_out && (
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-slate-500">OUT:</span>
                              <span className="text-sm font-mono font-medium text-red-600">{r.proposed_out}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {r.reason && (
                      <div className="bg-slate-50 rounded p-3">
                        <p className="text-xs text-slate-500 mb-1">Reason</p>
                        <p className="text-sm text-slate-700">{r.reason}</p>
                      </div>
                    )}
                  </div>

                  {r.status === 'pending' && (
                    <div className="flex flex-col space-y-2 ml-6">
                      <Button
                        data-testid={`approve-${r.id}`}
                        disabled={actingId === r.id}
                        onClick={() => act(r.id, 'approved')}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        {actingId === r.id ? '…' : 'Approve'}
                      </Button>
                      <Button
                        variant="outline"
                        data-testid={`reject-${r.id}`}
                        disabled={actingId === r.id}
                        onClick={() => act(r.id, 'rejected')}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                      >
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bulk Dialog */}
        {bulkModal.open && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4 shadow-xl">
              <div className="flex justify-between items-center border-b pb-3">
                <h3 className="font-bold text-slate-900">
                  {bulkModal.action === 'approve' ? 'Bulk Approve Regularizations' : 'Bulk Reject Regularizations'}
                </h3>
                <button onClick={() => setBulkModal({ open: false, action: 'approve' })}><X className="w-5 h-5 text-slate-400" /></button>
              </div>

              <p className="text-xs text-slate-600">
                Are you sure you want to {bulkModal.action} {selectedIds.length} regularization request{selectedIds.length > 1 ? 's' : ''}?
              </p>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Remarks {bulkModal.action === 'reject' ? '(Required)' : '(Optional)'}</label>
                <textarea
                  rows={3}
                  value={bulkRemarks}
                  onChange={e => setBulkRemarks(e.target.value)}
                  placeholder={bulkModal.action === 'reject' ? 'Enter rejection reason...' : 'Approval notes...'}
                  className="w-full border border-slate-300 rounded p-2 text-xs"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setBulkModal({ open: false, action: 'approve' })}>Cancel</Button>
                <Button
                  onClick={executeBulk}
                  disabled={bulkProcessing || (bulkModal.action === 'reject' && !bulkRemarks.trim())}
                  className={bulkModal.action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
                >
                  {bulkProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Confirm {bulkModal.action === 'approve' ? 'Approve' : 'Reject'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default RegularizationInbox;

